import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatEUR, firstCategoryWithProducts, isNetworkError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Trash2, Receipt, Percent, FileText, Printer, X, Plus, Minus, Coffee, Ticket, Calculator, Maximize2, StickyNote } from "lucide-react";
import ScontrinoPreview from "@/components/ScontrinoPreview";
import CustomerLoyaltyPicker from "@/components/CustomerLoyaltyPicker";
import DiscountModal from "@/components/pos/DiscountModal";
import HiddenGestionaleModal from "@/components/pos/HiddenGestionaleModal";
import InlinePOSKeypad from "@/components/pos/InlinePOSKeypad";
import OperatorSwitcher from "@/components/OperatorSwitcher";
import usePrecontoPrinter from "@/lib/usePrecontoPrinter";
import { printComandeViaQz, thermalPrintError } from "@/lib/printService";
import { checkStockAlert } from "@/lib/stockAlert";
import { TABLE_STATUS, tableStatusOf } from "@/lib/tableStatus";
import { COURSE_OPTIONS, groupLinesByCourse } from "@/lib/comanda";
import { useAuth } from "@/lib/auth";
import { emitFiscalPaper } from "@/lib/fiscal";
import { authorizeCardIfNeeded } from "@/lib/paymentPos";
import { newOfflineId } from "@/lib/offlineQueue";
import { recoverUnreachableSale, SERVER_ATTEMPT } from "@/lib/offlineSales";

export default function POSRestaurant() {
  const nav = useNavigate();
  const { user } = useAuth() || {};
  const [tenant, setTenant] = useState(null);
  const [tables, setTables] = useState([]);
  const [cats, setCats] = useState([]);
  const [prods, setProds] = useState([]);
  const [activeCat, setActiveCat] = useState(null);
  const [activeTable, setActiveTable] = useState(null);
  const [order, setOrder] = useState(null); // current open order
  const [takeaway, setTakeaway] = useState(false);
  const [showPay, setShowPay] = useState(false);
  const [showLottery, setShowLottery] = useState(false);
  const [lottery, setLottery] = useState("");
  const [lastClosed, setLastClosed] = useState(null);
  const [showScontrino, setShowScontrino] = useState(false);
  const [customer, setCustomer] = useState(null);
  const [redeemPoints, setRedeemPoints] = useState(0);
  const [showFreePrice, setShowFreePrice] = useState(false);
  const [showDiscount, setShowDiscount] = useState(false);
  const [showGestionale, setShowGestionale] = useState(false);
  const [keypadOpen, setKeypadOpen] = useState(false);
  const [showCartFull, setShowCartFull] = useState(false);
  const [noteEditIdx, setNoteEditIdx] = useState(null);
  const [tableNoteOpen, setTableNoteOpen] = useState(false);
  const [mergePick, setMergePick] = useState([]);
  const [activeCourse, setActiveCourse] = useState("");
  // Chip suggerimento — se il tenant ne ha di personalizzati usa quelli, altrimenti i 6 default
  const chips = useMemo(() => {
    const custom = tenant?.line_note_suggestions;
    if (custom && custom.length > 0) return custom;
    return [
      { label: "ben cotto", price_delta: 0 },
      { label: "al sangue", price_delta: 0 },
      { label: "senza cipolla", price_delta: 0 },
      { label: "senza glutine", price_delta: 0 },
      { label: "poco piccante", price_delta: 0 },
      { label: "senza sale", price_delta: 0 },
    ];
  }, [tenant]);

  const loadTables = () => api.get("/tables").then((r) => setTables(Array.isArray(r.data) ? r.data : []));
  const reloadProducts = () => api.get("/products?mode=ristorante").then((r) => setProds(Array.isArray(r.data) ? r.data : []));
  useEffect(() => {
    api.get("/tenant/me").then((r) => setTenant(r.data));
    loadTables();
    api.get("/categories?mode=ristorante").then((r) => setCats(Array.isArray(r.data) ? r.data : []));
    api.get("/products?mode=ristorante").then((r) => setProds(Array.isArray(r.data) ? r.data : []));
    const t = setInterval(loadTables, 8000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (activeCat == null && cats.length && prods.length) setActiveCat(firstCategoryWithProducts(cats, prods));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cats, prods]);

  const filteredProds = useMemo(() => (Array.isArray(prods) ? prods : []).filter((p) => p.category_id === activeCat && p.visible !== false), [prods, activeCat]);

  const openTable = async (table) => {
    setActiveTable(table);
    setTakeaway(false);
    if (table.open_order_id) {
      const r = await api.get(`/orders/${table.open_order_id}`);
      setOrder(r.data);
    } else {
      setOrder({ lines: [], total: 0, subtotal: 0, table_id: table.id, mode: "ristorante" });
    }
  };

  const startTakeaway = () => {
    setActiveTable(null);
    setTakeaway(true);
    setOrder({ lines: [], total: 0, subtotal: 0, mode: "ristorante", takeaway: true });
  };

  const addProduct = (p) => {
    if (!order) {
      toast.error("Seleziona un tavolo o avvia un asporto");
      return;
    }
    // Anticipa il decremento visivo dello stock per un feedback immediato
    const willDecrement = (p.stock_current !== null && p.stock_current !== undefined);
    const lines = [...order.lines];
    const existing = lines.findIndex((l) => l.product_id === p.id && !l.note && (l.extras || []).length === 0 && (l.course || "") === (activeCourse || ""));
    if (existing >= 0) {
      lines[existing] = { ...lines[existing], qty: lines[existing].qty + 1 };
    } else {
      lines.push({
        product_id: p.id, name: p.name, qty: 1, price: p.price, vat: p.vat, extras: [], discount_pct: 0,
        print_to: p.effective_print_to || p.print_to,
        print_station: p.effective_print_station || p.print_station || p.effective_print_to || p.print_to,
        category_id: p.category_id,
        course: activeCourse || null,
      });
    }
    recomputeAndSet(lines);
    // Stock alert (proiettato: quanto sarà dopo la chiusura)
    if (willDecrement) {
      const alreadyInCart = order.lines
        .filter((l) => l.product_id === p.id)
        .reduce((s, l) => s + (l.qty || 0), 0);
      const projected = p.stock_current - (alreadyInCart + 1);
      checkStockAlert({ ...p, stock_current: projected });
    }
  };

  const recomputeAndSet = (lines) => {
    let sub = 0;
    const vatBreak = {};
    for (const l of lines) {
      let lt = l.price * l.qty;
      if (l.discount_pct) lt *= (1 - l.discount_pct / 100);
      for (const e of (l.extras || [])) lt += parseFloat(e.price || 0);
      sub += lt;
      vatBreak[l.vat] = (vatBreak[l.vat] || 0) + lt;
    }
    setOrder({ ...order, lines, subtotal: sub, total: sub, vat_break: vatBreak });
  };

  const incLine = (idx, delta) => {
    const lines = [...order.lines];
    lines[idx] = { ...lines[idx], qty: Math.max(0, lines[idx].qty + delta) };
    if (lines[idx].qty === 0) lines.splice(idx, 1);
    recomputeAndSet(lines);
  };
  const delLine = (idx) => {
    const lines = [...order.lines]; lines.splice(idx, 1); recomputeAndSet(lines);
  };
  const updateLineNote = (idx, note) => {
    const lines = [...order.lines];
    lines[idx] = { ...lines[idx], note: note || null };
    recomputeAndSet(lines);
  };

  /**
   * Applica un chip alla riga:
   *  - price_delta === 0 → salva come nota testuale (appende se esiste già)
   *  - price_delta !== 0 → aggiunge un elemento in `extras` con {name: label, price: delta}
   *    (rimuove eventuali extras con lo stesso name prima di aggiungere, evita duplicati)
   */
  const applyChip = (idx, chip) => {
    if (!chip.label) return;
    const lines = [...order.lines];
    const cur = { ...lines[idx] };
    if (!chip.price_delta || chip.price_delta === 0) {
      // Nota testuale — sovrascrive
      cur.note = chip.label;
    } else {
      const exs = (Array.isArray(cur.extras) ? cur.extras : []).filter((e) => e.name !== chip.label);
      exs.push({ name: chip.label, price: chip.price_delta });
      cur.extras = exs;
    }
    lines[idx] = cur;
    recomputeAndSet(lines);
  };

  const removeExtra = (lineIdx, exName) => {
    const lines = [...order.lines];
    lines[lineIdx] = { ...lines[lineIdx], extras: (lines[lineIdx].extras || []).filter((e) => e.name !== exName) };
    recomputeAndSet(lines);
  };

  const updateTableNote = (val) => {
    if (!order) return;
    setOrder({ ...order, notes: val || null });
  };

  const addFreePriceLine = (amountOrObj) => {
    // supporta chiamata da InlinePOSKeypad (number) e da FreePriceModal (object)
    let name, price, vat;
    if (typeof amountOrObj === "number") {
      name = "Prezzo libero"; price = amountOrObj; vat = 22;
    } else {
      ({ name, price, vat } = amountOrObj);
    }
    if (!order) {
      const lines = [{ product_id: null, name, qty: 1, price, vat, extras: [], discount_pct: 0, print_to: null }];
      setActiveTable(null);
      setTakeaway(true);
      setOrder({ lines, total: price, subtotal: price, mode: "ristorante", takeaway: true, vat_break: { [vat]: price } });
      setShowFreePrice(false);
      toast.success(`Aggiunto: ${name} · ${formatEUR(price)}`);
      return;
    }
    const lines = [...order.lines, { product_id: null, name, qty: 1, price, vat, extras: [], discount_pct: 0, print_to: null }];
    recomputeAndSet(lines);
    setShowFreePrice(false);
    toast.success(`Aggiunto: ${name} · ${formatEUR(price)}`);
  };

  const applyOrderDiscount = ({ mode, value }) => {
    if (!order?.lines?.length) return;
    let pct = 0;
    if (mode === "pct") pct = Math.min(100, Math.max(0, value));
    else {
      const currentSub = order.lines.reduce((s, l) => {
        let lt = l.price * l.qty;
        if (l.discount_pct) lt *= (1 - l.discount_pct / 100);
        for (const e of (l.extras || [])) lt += parseFloat(e.price || 0);
        return s + lt;
      }, 0);
      pct = currentSub > 0 ? Math.min(100, (value / currentSub) * 100) : 0;
    }
    const lines = order.lines;
    let sub = 0;
    const vatBreak = {};
    for (const l of lines) {
      let lt = l.price * l.qty;
      if (l.discount_pct) lt *= (1 - l.discount_pct / 100);
      for (const e of (l.extras || [])) lt += parseFloat(e.price || 0);
      sub += lt;
      vatBreak[l.vat] = (vatBreak[l.vat] || 0) + lt;
    }
    if (pct > 0) {
      sub = sub * (1 - pct / 100);
      for (const k in vatBreak) vatBreak[k] = vatBreak[k] * (1 - pct / 100);
    }
    setOrder({ ...order, discount_pct: pct, subtotal: sub, total: sub, vat_break: vatBreak });
    setShowDiscount(false);
    toast.success(pct > 0 ? `Sconto ${pct.toFixed(1)}% applicato` : "Sconto rimosso");
  };

  const openGestionale = async () => {
    // Salva prima l'ordine se non esiste
    const saved = await saveOrder();
    if (!saved) return;
    setShowGestionale(true);
  };

  const onGestionaleClosed = async (closedOrder) => {
    // stampa comanda associata (simulata)
    const byPrinter = (closedOrder.lines || []).reduce((acc, l) => {
      const k = l.print_to || "_";
      (acc[k] = acc[k] || []).push(l);
      return acc;
    }, {});
    const where = Object.keys(byPrinter).filter((k) => k !== "_").join(" + ") || "—";
    if (where !== "—") toast.info(`Comanda gestionale inviata → ${where}`);
    setLastClosed({ ...closedOrder });
    setShowScontrino(true);
    setOrder(null);
    setActiveTable(null);
    setTakeaway(false);
    setLottery("");
    setCustomer(null);
    setRedeemPoints(0);
    loadTables();
    reloadProducts();
  };

  const { hasPreconto, gestionaleEnabled } = usePrecontoPrinter();
  const openGestionaleRef = React.useRef(null);
  openGestionaleRef.current = openGestionale;
  useEffect(() => {
    if (!gestionaleEnabled) return undefined;
    const h = () => openGestionaleRef.current?.();
    window.addEventListener("velta-gestionale-open", h);
    return () => window.removeEventListener("velta-gestionale-open", h);
  }, [gestionaleEnabled]);

  const saveOrder = async ({ quiet = false, offlineId = null } = {}) => {
    if (!order?.lines?.length) { toast.error("Aggiungi almeno un articolo"); return null; }
    const attempt = quiet ? SERVER_ATTEMPT : undefined;
    try {
      if (order.id) {
        // PATCH: aggiorna un ordine già aperto (tavolo riaperto o dopo comanda)
        const r = await api.patch(`/orders/${order.id}`, {
          lines: (Array.isArray(order.lines) ? order.lines : []).map(l => ({ ...l })),
          discount_pct: order.discount_pct || 0,
          notes: order.notes || null,
        }, attempt);
        setOrder(r.data);
        loadTables();
        return r.data;
      }
      const payload = {
        mode: "ristorante",
        table_id: activeTable?.id,
        takeaway,
        lines: (Array.isArray(order.lines) ? order.lines : []).map(l => ({ ...l })),
        discount_pct: order.discount_pct || 0,
        notes: order.notes || null,
      };
      if (offlineId) payload.offline_id = offlineId;
      if (customer) payload.customer_id = customer.id;
      const r = await api.post("/orders", payload, attempt);
      setOrder(r.data);
      loadTables();
      return r.data;
    } catch (e) {
      if (quiet && (isNetworkError(e) || e?.isNetworkError)) throw e;
      toast.error("Errore salvataggio");
      return null;
    }
  };

  const sendComande = async () => {
    const saved = await saveOrder();
    if (!saved) return;
    const byPrinter = (saved.lines || []).reduce((acc, l) => { (acc[l.print_to || "_"] = acc[l.print_to || "_"] || []).push(l); return acc; }, {});
    const labelize = (k) => k === "kitchen" ? "cucina" : k === "bar" ? "bar" : k;
    const where = Object.keys(byPrinter).filter(k => k !== "_").map(labelize).join(" + ") || "—";
    toast.success(`Comanda inviata → ${where}`);
    printComandeViaQz({
      order: saved,
      tenant,
      tableName: activeTable?.name,
      roomName: activeTable?.room,
      operator: saved.last_operator_name || saved.operator_name || user?.name,
    })
      .then((res) => {
        if (res.ok) toast.success(`Comanda stampata: ${(Array.isArray(res.printed) ? res.printed : []).map((p) => p.printer).join(", ")}`);
        else toast.error(thermalPrintError(res), { duration: 8000 });
      })
      .catch((e) => toast.error(String(e?.message || e)));
  };

  const printPreconto = async () => {
    const saved = await saveOrder();
    if (!saved) return;
    await api.post(`/orders/${saved.id}/preconto`);
    setLastClosed({ ...saved, receipt_type: "preconto" });
    setShowScontrino(true);
  };

  const closeFiscal = async (paymentMethod, breakdown) => {
    const pos = await authorizeCardIfNeeded({ method: paymentMethod, breakdown, amount: order?.total || 0 });
    if (!pos.ok) { toast.error(pos.message || "Pagamento carta non autorizzato"); return; }
    if (pos.mode === "pos") toast.success("Carta autorizzata sul POS");
    const offlineId = order?.offline_id || newOfflineId();
    let saved = null;
    try {
      saved = await saveOrder({ quiet: true, offlineId });
    } catch (e) {
      const off = await recoverUnreachableSale(e, {
        tenant, offline_id: offlineId, server_order_id: order?.id || null,
        mode: "ristorante", lines: order?.lines || [],
        payment_method: paymentMethod, payment_breakdown: breakdown || null,
        discount_pct: order?.discount_pct || 0, customer_id: customer?.id || null,
        notes: order?.notes || null, table_id: activeTable?.id || null,
        lottery_code: lottery || null, redeem_points: redeemPoints,
      });
      if (off.printed) {
        setLastClosed(off.order);
        setShowPay(false);
        setShowScontrino(true);
        setOrder(null);
        setActiveTable(null);
        setTakeaway(false);
        setLottery("");
        setCustomer(null);
        setRedeemPoints(0);
      }
      return;
    }
    if (!saved) return;
    const closeBody = {
      payment_method: paymentMethod,
      payment_breakdown: breakdown || null,
      lottery_code: lottery || null,
    };
    try {
      // Conferma operatore prima di applicare il riscatto
      if (redeemPoints > 0 && customer) {
        const previewR = await api.get(`/customers/${customer.id}/redeem-preview`);
        const thr = previewR.data.threshold;
        const valuePerThr = previewR.data.value_per_threshold;
        const discount = (redeemPoints / thr) * valuePerThr;
        if (window.confirm(`Applicare sconto fedeltà di ${discount.toFixed(2)}€ scalando ${redeemPoints} punti al cliente ${customer.name}?`)) {
          closeBody.redeem_points = redeemPoints;
        }
      }
      const r = await api.post(`/orders/${saved.id}/close-fiscal`, closeBody, SERVER_ATTEMPT);
      setLastClosed(r.data);
      setShowPay(false);
      setShowScontrino(true);
      setOrder(null);
      setActiveTable(null);
      setTakeaway(false);
      setLottery("");
      setCustomer(null);
      setRedeemPoints(0);
      loadTables();
      reloadProducts();
      toast.success(`Scontrino #${r.data.receipt_number} emesso (gestionale). Attendi la carta sulla Epson…`);
      await emitFiscalPaper({ tenant, order: r.data });
    } catch (err) {
      const off = await recoverUnreachableSale(err, {
        tenant, offline_id: offlineId, server_order_id: saved?.id || order?.id || null,
        mode: "ristorante", lines: saved?.lines || order?.lines || [],
        payment_method: paymentMethod, payment_breakdown: breakdown || null,
        discount_pct: order?.discount_pct || 0, customer_id: customer?.id || null,
        notes: order?.notes || null, table_id: activeTable?.id || saved?.table_id || null,
        lottery_code: lottery || null, redeem_points: closeBody.redeem_points || 0,
      });
      if (off.printed) {
        setLastClosed(off.order);
        setShowPay(false);
        setShowScontrino(true);
        setOrder(null);
        setActiveTable(null);
        setTakeaway(false);
        setLottery("");
        setCustomer(null);
        setRedeemPoints(0);
        return;
      }
      if (off.handled) return;
      toast.error(err?.response?.data?.detail || "Errore chiusura fiscale");
    }
  };

  const cancelOrder = async () => {
    if (!order?.id) { setOrder(null); setActiveTable(null); setTakeaway(false); return; }
    if (!window.confirm("Annullare conto aperto?")) return;
    await api.delete(`/orders/${order.id}`);
    toast.success("Conto annullato"); setOrder(null); setActiveTable(null); setTakeaway(false); loadTables();
  };

  const toggleMerge = (id) => {
    setMergePick((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id].slice(-6)));
  };

  const doMerge = async () => {
    if (mergePick.length < 2) return toast.error("Seleziona almeno 2 tavoli da unire");
    try {
      await api.post("/tables/merge", { table_ids: mergePick });
      toast.success("Tavoli uniti");
      setMergePick([]);
      loadTables();
    } catch (e) { toast.error(e?.response?.data?.detail || "Unione non riuscita"); }
  };

  const doSplit = async (id) => {
    try {
      await api.post(`/tables/${id}/split`);
      toast.success("Tavoli separati");
      loadTables();
    } catch (e) { toast.error(e?.response?.data?.detail || "Separazione non riuscita"); }
  };

  const grouped = tables.reduce((acc, t) => { if (t.hidden_on_map || t.joined_parent_id) return acc; (acc[t.room] = acc[t.room] || []).push(t); return acc; }, {});

  return (
    <div className="h-screen bg-velta-bg flex flex-col overflow-hidden">
      {/* Top bar */}
      <header className="h-14 border-b border-velta-border bg-white flex items-center px-4 gap-4 shrink-0">
        <Link to="/cassa" className="inline-flex items-center gap-2 text-velta-mute hover:text-velta-ink text-sm font-medium">
          <ArrowLeft className="w-4 h-4" /> Modalità
        </Link>
        <div className="h-6 w-px bg-velta-border" />
        <div className="font-heading font-semibold tracking-tight">Velta One · Ristorante</div>
        <div className="ml-auto flex items-center gap-2 text-sm text-velta-mute">
          <OperatorSwitcher compact />
          <Coffee className="w-4 h-4" /> {tenant?.business_name}
        </div>
      </header>

      <div className="flex-1 grid grid-cols-12 overflow-hidden">
        {/* LEFT: tables list */}
        <aside className="col-span-2 border-r border-velta-border bg-white overflow-auto pos-scroll">
          <div className="p-3">
            <button data-testid="pos-takeaway-btn" onClick={startTakeaway} className={`w-full h-12 mb-3 rounded-md font-medium text-sm flex items-center justify-center gap-2 btn-tactile ${takeaway ? "bg-velta-ink text-white" : "bg-velta-bg border border-velta-border text-velta-ink hover:bg-velta-border"}`}>
              <Ticket className="w-4 h-4" /> Asporto
            </button>
            <div className="flex flex-wrap gap-1 mb-3 px-1">
              {Object.entries(TABLE_STATUS).map(([k, v]) => (
                <span key={k} className="text-[9px] font-mono inline-flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full" style={{ background: v.swatch }} /> {v.label}
                </span>
              ))}
            </div>
            <div className="flex gap-1 mb-3">
              <button type="button" data-testid="pos-tables-merge" onClick={doMerge} className="flex-1 h-9 text-[10px] font-medium border border-velta-border rounded-md">Unisci {mergePick.length ? `(${mergePick.length})` : ""}</button>
              <button type="button" data-testid="pos-tables-split" onClick={() => activeTable?.id && doSplit(activeTable.id)} className="flex-1 h-9 text-[10px] font-medium border border-velta-border rounded-md">Separa</button>
            </div>
            <p className="text-[9px] text-velta-mute mb-3 px-1">Tasto destro su un tavolo per selezionarlo, poi Unisci. Separa slega i tavoli del conto attivo.</p>
            {Object.entries(grouped).map(([room, ts]) => (
              <div key={room} className="mb-4">
                <div className="text-[10px] font-mono uppercase tracking-widest text-velta-mute mb-2 px-1">{room}</div>
                <div className="grid grid-cols-2 gap-2">
                  {(Array.isArray(ts) ? ts : []).map((t) => {
                    const isActive = activeTable?.id === t.id;
                    const st = tableStatusOf(t);
                    const picking = mergePick.includes(t.id);
                    return (
                      <button
                        key={t.id}
                        data-testid={`pos-table-${t.name}`}
                        onClick={() => openTable(t)}
                        onContextMenu={(e) => { e.preventDefault(); toggleMerge(t.id); }}
                        className={`h-16 rounded-md font-heading font-bold text-lg btn-tactile border-2 ${
                          isActive ? "bg-[#1E2D5B] text-white border-[#1E2D5B]" : picking ? "ring-2 ring-[#1E2D5B] " + st.className : st.className
                        }`}
                        title={`${st.label}${t.opened_by ? ` · ${t.opened_by}` : ""}${t.joined_ids?.length ? " · unito" : ""}`}
                      >
                        {t.name}{t.joined_ids?.length ? "+" : ""}
                        <div className="text-[9px] font-mono font-normal opacity-80">{st.label}{t.open_order_id && !isActive ? ` · ${formatEUR(t.open_total)}` : ""}</div>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </aside>

        {/* CENTER: categories + products */}
        <section className="col-span-6 flex flex-col overflow-hidden">
          <div className="px-3 py-2 border-b border-velta-border bg-white flex gap-1 overflow-x-auto pos-scroll" data-testid="pos-course-bar">
            {COURSE_OPTIONS.map((c) => (
              <button
                key={c.id || "none"}
                type="button"
                onClick={() => setActiveCourse(c.id)}
                className={`shrink-0 h-8 px-3 rounded-full text-[11px] font-medium ${activeCourse === c.id ? "bg-velta-ink text-white" : "bg-velta-bg border border-velta-border"}`}
              >
                {c.label}
              </button>
            ))}
          </div>
          {/* Categories */}
          <div className="h-20 border-b border-velta-border bg-white flex gap-2 overflow-x-auto pos-scroll p-3">
            {(Array.isArray(cats) ? cats : []).map((c) => (
              <button
                key={c.id}
                data-testid={`pos-cat-${c.id}`}
                onClick={() => setActiveCat(c.id)}
                className={`shrink-0 h-14 px-5 rounded-md font-medium btn-tactile flex items-center gap-2 ${activeCat === c.id ? "text-white" : "bg-white border border-velta-border text-velta-ink hover:border-velta-ink"}`}
                style={activeCat === c.id ? { background: c.color } : {}}
              >
                <div className="w-2 h-2 rounded-sm" style={{ background: activeCat === c.id ? "rgba(255,255,255,0.5)" : c.color }} />
                {c.name}
              </button>
            ))}
          </div>

          {/* Products grid */}
          <div className="flex-1 overflow-auto pos-scroll p-3">
            <div className="grid grid-cols-3 lg:grid-cols-4 gap-3">
              {(Array.isArray(filteredProds) ? filteredProds : []).map((p) => {
                const cat = cats.find(c => c.id === p.category_id);
                const color = p.color || cat?.color || "#0A0A0A";
                const lowStock = p.stock_limit && p.stock_current !== null && p.stock_current <= p.stock_limit;
                return (
                  <button
                    key={p.id}
                    data-testid={`pos-prod-${p.id}`}
                    onClick={() => addProduct(p)}
                    className="relative bg-white border border-velta-border rounded-md p-4 h-28 text-left flex flex-col justify-between hover:border-velta-ink btn-tactile active:scale-[0.97] overflow-hidden"
                  >
                    <div className="absolute top-0 left-0 right-0 h-1.5" style={{ background: color }} />
                    {lowStock && <div className="absolute top-2 right-2 text-[9px] font-mono uppercase tracking-widest text-velta-warning">Scorta {p.stock_current}</div>}
                    <div className="font-medium text-sm leading-tight mt-2">{p.name}</div>
                    {p.show_price && <div className="font-mono font-bold text-lg">{formatEUR(p.price)}</div>}
                  </button>
                );
              })}
              {filteredProds.length === 0 && <div className="col-span-4 p-12 text-center text-velta-mute">Nessun articolo. Vai in Gestionale → Articoli.</div>}
            </div>
          </div>
        </section>

        {/* RIGHT: cart */}
        <aside className="col-span-4 border-l border-velta-border bg-white flex flex-col overflow-hidden">
          <div className="p-3 border-b border-velta-border flex items-center justify-between gap-2">
            <div className="min-w-0">
              <div className="text-[10px] font-mono uppercase tracking-widest text-velta-mute">{takeaway ? "Asporto" : activeTable ? `Tavolo ${activeTable.name}` : "Nessun tavolo"}</div>
              <div className="flex items-center gap-2 mt-0.5">
                <h2 className="font-heading font-semibold text-lg">Conto</h2>
                {order?.lines?.length ? <span className="text-[10px] font-mono text-velta-mute">· {order.lines.length} art. · {order.lines.reduce((s, l) => s + (l.qty || 0), 0)} pz</span> : null}
                {order?.id && <span className="text-[10px] font-mono text-velta-mute">#{order.id.slice(0, 8)}</span>}
              </div>
              {order?.notes && !tableNoteOpen && (
                <button
                  data-testid="pos-table-note-badge"
                  onClick={() => setTableNoteOpen(true)}
                  className="mt-1 text-[11px] px-2 py-0.5 bg-yellow-50 border border-yellow-300 rounded text-yellow-900 max-w-[240px] truncate flex items-center gap-1"
                  title="Modifica nota tavolo"
                >
                  <StickyNote className="w-3 h-3" /> {order.notes}
                </button>
              )}
            </div>
            <div className="flex flex-col gap-1 items-end shrink-0">
              {order && (
                <button
                  data-testid="pos-table-note-btn"
                  onClick={() => setTableNoteOpen((v) => !v)}
                  className={`h-9 px-3 rounded-md text-xs font-medium inline-flex items-center gap-1.5 btn-tactile ${order.notes ? "bg-yellow-100 border border-yellow-400 text-yellow-900" : "bg-white border border-velta-border text-velta-ink hover:border-velta-ink"}`}
                  title="Nota generale del tavolo (compleanno, allergie…)"
                >
                  <StickyNote className="w-3.5 h-3.5" /> Nota tavolo
                </button>
              )}
              {order?.lines?.length ? (
                <button
                  data-testid="pos-expand-cart"
                  onClick={() => setShowCartFull(true)}
                  className="h-9 px-3 bg-velta-ink text-white rounded-md text-xs font-medium inline-flex items-center gap-1.5 btn-tactile"
                  title="Vedi tutto a schermo intero"
                >
                  <Maximize2 className="w-3.5 h-3.5" /> Espandi
                </button>
              ) : null}
            </div>
          </div>

          {tableNoteOpen && order && (
            <div className="border-b border-yellow-300 bg-yellow-50 px-3 py-2" data-testid="pos-table-note-panel">
              <label className="block text-[10px] font-mono uppercase tracking-widest text-yellow-900 mb-1">Nota tavolo (compleanno, allergie, celiaco…)</label>
              <input
                data-testid="pos-table-note-input"
                autoFocus
                type="text"
                maxLength={120}
                defaultValue={order.notes || ""}
                onBlur={(e) => { updateTableNote(e.target.value.trim()); setTableNoteOpen(false); }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") { updateTableNote(e.currentTarget.value.trim()); setTableNoteOpen(false); }
                  else if (e.key === "Escape") { setTableNoteOpen(false); }
                }}
                placeholder="Es. compleanno · allergia noci · celiaco · niente sale"
                className="h-9 w-full bg-white border border-yellow-400 rounded px-2 text-sm outline-none focus:border-velta-ink"
              />
              <div className="flex flex-wrap gap-1 mt-1.5">
                {["compleanno", "allergia noci", "celiaco", "senza glutine", "vegetariani", "bambini"].map((sug) => (
                  <button
                    key={sug}
                    data-testid={`pos-table-note-sug-${sug.replace(/ /g, '-')}`}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => { updateTableNote(sug); setTableNoteOpen(false); }}
                    className="text-[10px] px-2 h-6 bg-white border border-yellow-300 rounded-full hover:bg-yellow-200"
                  >
                    {sug}
                  </button>
                ))}
                {order.notes && (
                  <button
                    data-testid="pos-table-note-clear"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => { updateTableNote(""); setTableNoteOpen(false); }}
                    className="text-[10px] px-2 h-6 bg-white border border-velta-error text-velta-error rounded-full hover:bg-velta-error hover:text-white ml-auto"
                  >
                    Rimuovi
                  </button>
                )}
              </div>
            </div>
          )}

          <div className="flex-1 overflow-auto pos-scroll p-1.5 min-h-[180px]" data-testid="pos-cart-list">
            {!order?.lines?.length ? (
              <div className="text-center text-velta-mute text-sm p-8">Seleziona un tavolo e gli articoli per iniziare.</div>
            ) : (
              <div className="space-y-2">
                {groupLinesByCourse(order.lines).map((g) => (
                  <div key={g.key}>
                    {g.label && <div className="text-[9px] font-mono uppercase tracking-widest text-velta-mute px-1 py-0.5">{g.label}</div>}
                    {g.items.map(({ line: l, index: i }) => (
                  <div key={i} className={`border rounded-md bg-white ${l.note || (l.extras || []).length > 0 || noteEditIdx === i ? "border-velta-ink" : "border-velta-border"}`} data-testid={`pos-cart-line-${i}`}>
                    <div className="px-2 py-1.5 flex items-center gap-1.5">
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-[13px] leading-tight truncate">{l.name}</div>
                        {l.note && noteEditIdx !== i && (
                          <div className="text-[10px] text-velta-ink leading-tight truncate italic" title={l.note}>↳ {l.note}</div>
                        )}
                        {(Array.isArray(l.extras) ? l.extras : []).map((ex) => (
                          <div key={ex.name} className="text-[10px] flex items-center gap-1 leading-tight">
                            <span className={ex.price < 0 ? "text-velta-success" : "text-velta-warning"}>
                              {ex.price > 0 ? "+" : ""}{ex.name} ({ex.price > 0 ? "+" : ""}{formatEUR(ex.price)})
                            </span>
                            <button onClick={() => removeExtra(i, ex.name)} className="text-velta-mute hover:text-velta-error" title="Rimuovi">
                              <X className="w-2.5 h-2.5" />
                            </button>
                          </div>
                        ))}
                      </div>
                      <div className="inline-flex items-center border border-velta-border rounded bg-velta-bg shrink-0">
                        <button data-testid={`pos-line-minus-${i}`} onClick={() => incLine(i, -1)} className="w-7 h-7 flex items-center justify-center hover:bg-white"><Minus className="w-3 h-3" /></button>
                        <span className="w-6 text-center font-mono font-bold text-sm">{l.qty}</span>
                        <button data-testid={`pos-line-plus-${i}`} onClick={() => incLine(i, 1)} className="w-7 h-7 flex items-center justify-center hover:bg-white"><Plus className="w-3 h-3" /></button>
                      </div>
                      <div className="w-14 text-right font-mono font-bold text-[13px] tabular-nums shrink-0">
                        {formatEUR(l.price * l.qty + (l.extras || []).reduce((s, e) => s + parseFloat(e.price || 0), 0))}
                      </div>
                      <button
                        data-testid={`pos-line-note-${i}`}
                        onClick={() => setNoteEditIdx(noteEditIdx === i ? null : i)}
                        className={`h-7 w-7 rounded flex items-center justify-center shrink-0 ${l.note || (l.extras || []).length ? "text-velta-ink bg-blue-50" : "text-velta-mute hover:bg-velta-bg"}`}
                        title="Nota / aggiunta per la cucina"
                      >
                        <StickyNote className="w-3.5 h-3.5" />
                      </button>
                      <button
                        data-testid={`pos-line-del-${i}`}
                        onClick={() => delLine(i)}
                        className="text-velta-error hover:bg-red-50 h-7 w-7 rounded flex items-center justify-center shrink-0"
                        title="Elimina riga"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    {noteEditIdx === i && (
                      <div className="px-2 pb-2 pt-0.5 border-t border-velta-border/50 bg-velta-bg/40">
                        <input
                          autoFocus
                          data-testid={`pos-line-note-input-${i}`}
                          type="text"
                          maxLength={80}
                          defaultValue={l.note || ""}
                          onBlur={(e) => { updateLineNote(i, e.target.value.trim()); setNoteEditIdx(null); }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") { updateLineNote(i, e.currentTarget.value.trim()); setNoteEditIdx(null); }
                            else if (e.key === "Escape") { setNoteEditIdx(null); }
                          }}
                          placeholder="Nota libera per la cucina..."
                          className="h-9 w-full bg-white border border-velta-border rounded px-2 text-[12px] outline-none focus:border-velta-ink"
                        />
                        <div className="flex flex-wrap gap-1 mt-1.5">
                          {(Array.isArray(chips) ? chips : []).map((c) => (
                            <button
                              key={c.label}
                              data-testid={`pos-line-note-sug-${i}-${c.label.replace(/ /g, '-')}`}
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={() => { applyChip(i, c); setNoteEditIdx(null); }}
                              className={`text-[10px] px-2 h-6 bg-white border rounded-full ${c.price_delta > 0 ? "border-velta-warning text-velta-warning" : c.price_delta < 0 ? "border-velta-success text-velta-success" : "border-velta-border"} hover:bg-velta-ink hover:text-white hover:border-velta-ink`}
                            >
                              {c.label}{c.price_delta ? ` ${c.price_delta > 0 ? "+" : ""}${c.price_delta}€` : ""}
                            </button>
                          ))}
                          {(l.note || (l.extras || []).length > 0) && (
                            <button
                              data-testid={`pos-line-note-clear-${i}`}
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={() => {
                                const lines = [...order.lines];
                                lines[i] = { ...lines[i], note: null, extras: [] };
                                recomputeAndSet(lines);
                                setNoteEditIdx(null);
                              }}
                              className="text-[10px] px-2 h-6 bg-white border border-velta-error text-velta-error rounded-full hover:bg-velta-error hover:text-white ml-auto"
                            >
                              Rimuovi note/extras
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="border-t border-velta-border p-2.5 bg-velta-bg">
            <div className="flex items-center justify-between mb-1.5 select-none" data-testid="pos-total-row">
              <span className="text-[10px] font-mono uppercase tracking-widest text-velta-mute">Totale</span>
              <span data-testid="pos-total" className="font-mono font-bold text-2xl tracking-tighter">{formatEUR(order?.total || 0)}</span>
            </div>

            {/* Keypad toggle (compact by default) */}
            <button
              data-testid="pos-keypad-toggle"
              onClick={() => setKeypadOpen((v) => !v)}
              className="w-full h-8 mb-1.5 rounded bg-white border border-velta-border hover:border-velta-ink text-[11px] font-mono uppercase tracking-widest text-velta-mute inline-flex items-center justify-center gap-2"
            >
              <Calculator className="w-3.5 h-3.5" /> {keypadOpen ? "Nascondi tastiera" : "Prezzo libero / Sconto"}
            </button>
            {keypadOpen && (
              <div className="mb-1.5">
                <InlinePOSKeypad
                  onAddFreePrice={addFreePriceLine}
                  onApplyDiscount={applyOrderDiscount}
                  currentTotal={order?.total || 0}
                  compact
                />
              </div>
            )}
            <div className="grid grid-cols-2 gap-1.5">
              <button data-testid="pos-send-btn" disabled={!order?.lines?.length} onClick={sendComande} className={`h-10 bg-velta-ink text-white rounded-md text-xs font-medium btn-tactile disabled:opacity-40 ${hasPreconto ? "" : "col-span-2"}`}>Invia Comanda</button>
              {hasPreconto && (
                <button data-testid="pos-preconto-btn" disabled={!order?.lines?.length} onClick={printPreconto} className="h-10 bg-white border-2 border-velta-ink text-velta-ink hover:bg-velta-ink hover:text-white rounded-md text-xs font-medium flex items-center justify-center gap-1 btn-tactile disabled:opacity-40">
                  <FileText className="w-3.5 h-3.5" /> Preconto
                </button>
              )}
              <button data-testid="pos-lottery-btn" disabled={!order?.lines?.length} onClick={() => setShowLottery(true)} className="h-10 bg-white border border-velta-border text-velta-ink hover:border-velta-ink rounded-md text-xs font-medium flex items-center justify-center gap-1 btn-tactile disabled:opacity-40">
                <Ticket className="w-3.5 h-3.5" /> {lottery ? "Lott. ✓" : "Lotteria"}
              </button>
              <button data-testid="pos-cancel-btn" disabled={!order?.lines?.length} onClick={cancelOrder} className="h-10 bg-white border border-velta-error text-velta-error hover:bg-velta-error hover:text-white rounded-md text-xs font-medium btn-tactile disabled:opacity-40">Annulla</button>
              <button data-testid="pos-pay-btn" disabled={!order?.lines?.length} onClick={() => setShowPay(true)} className="col-span-2 h-12 bg-[#1E2D5B] hover:bg-[#162244] text-white rounded-md font-medium text-base flex items-center justify-center gap-2 btn-tactile disabled:opacity-40">
                <Receipt className="w-4 h-4" /> Scontrino Fiscale
              </button>
            </div>
          </div>
        </aside>
      </div>

      {showPay && (
        <PaymentModal
          total={order?.total || 0}
          onClose={() => setShowPay(false)}
          onConfirm={closeFiscal}
          customer={customer}
          setCustomer={setCustomer}
          redeemPoints={redeemPoints}
          setRedeemPoints={setRedeemPoints}
          tenant={tenant}
        />
      )}
      {showLottery && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50" onClick={() => setShowLottery(false)}>
          <div className="bg-white rounded-md w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-xl font-semibold">Codice Lotteria scontrini</h3>
              <button onClick={() => setShowLottery(false)}><X className="w-5 h-5" /></button>
            </div>
            <p className="text-sm text-velta-mute mb-3">Inserisci o scansiona il codice lotteria del cliente (8 caratteri).</p>
            <input
              data-testid="lottery-code-input"
              autoFocus value={lottery} onChange={(e) => setLottery(e.target.value.toUpperCase())} maxLength={8}
              className="h-14 w-full bg-velta-bg border-2 border-velta-border rounded-md px-4 font-mono text-2xl tracking-[0.3em] text-center uppercase outline-none focus:border-[#1E2D5B]"
              placeholder="XXXXXXXX"
            />
            <button data-testid="lottery-save-btn" onClick={() => { setShowLottery(false); toast.success("Codice lotteria salvato"); }} className="w-full mt-4 h-12 bg-[#1E2D5B] text-white rounded-md font-medium btn-tactile">Conferma</button>
          </div>
        </div>
      )}
      {showScontrino && <ScontrinoPreview order={lastClosed} tenant={tenant} fiscal={lastClosed?.receipt_type === "fiscal"} onClose={() => setShowScontrino(false)} />}
      <DiscountModal
        open={showDiscount}
        onClose={() => setShowDiscount(false)}
        onConfirm={applyOrderDiscount}
        target={{ type: "order", label: "Applicato al conto", currentTotal: order?.total || 0 }}
      />
      <HiddenGestionaleModal
        open={showGestionale}
        onClose={() => setShowGestionale(false)}
        orderId={order?.id}
        total={order?.total || 0}
        onSuccess={onGestionaleClosed}
      />
      {showCartFull && (
        <CartFullModal
          order={order}
          activeTable={activeTable}
          takeaway={takeaway}
          hasPreconto={hasPreconto}
          gestionaleEnabled={gestionaleEnabled}
          onClose={() => setShowCartFull(false)}
          onIncLine={incLine}
          onDelLine={delLine}
          onNoteUpdate={updateLineNote}
          onSendComande={async () => { await sendComande(); setShowCartFull(false); }}
          onPreconto={async () => { await printPreconto(); setShowCartFull(false); }}
          onPay={() => { setShowCartFull(false); setShowPay(true); }}
          onCancel={async () => { await cancelOrder(); setShowCartFull(false); }}
        />
      )}
    </div>
  );
}

function PaymentModal({ total, onClose, onConfirm, customer, setCustomer, redeemPoints, setRedeemPoints, tenant }) {
  const [method, setMethod] = useState("cash");
  const [cash, setCash] = useState("");
  const [mixed, setMixed] = useState({ cash: 0, card: 0, ticket: 0 });

  const change = method === "cash" ? Math.max(0, parseFloat(cash || 0) - total) : 0;
  const mixedSum = (mixed.cash || 0) + (mixed.card || 0) + (mixed.ticket || 0);

  const submit = () => {
    if (method === "mixed") {
      if (Math.abs(mixedSum - total) > 0.01) { toast.error("La somma non corrisponde al totale"); return; }
      onConfirm("mixed", mixed);
    } else {
      onConfirm(method);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50" onClick={onClose}>
      <div className="bg-white rounded-md w-full max-w-lg p-6 max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-heading text-2xl font-semibold">Pagamento</h3>
          <button data-testid="pay-close" onClick={onClose}><X className="w-6 h-6" /></button>
        </div>
        <div className="bg-velta-bg border border-velta-border rounded-md p-4 mb-5 text-center">
          <div className="text-xs font-mono uppercase tracking-widest text-velta-mute mb-1">Totale</div>
          <div className="font-mono font-bold text-4xl">{formatEUR(total)}</div>
        </div>
        <div className="mb-4">
          <CustomerLoyaltyPicker
            selected={customer}
            onSelect={setCustomer}
            redeem={redeemPoints}
            onRedeemChange={setRedeemPoints}
            testid="rist-loy"
            mode="ristorante"
          />
        </div>
        <div className="grid grid-cols-4 gap-2 mb-5">
          {[
            { v: "cash", l: "Contanti" }, { v: "card", l: "Carta" }, { v: "ticket", l: "Ticket" }, { v: "mixed", l: "Misto" },
            ...((Array.isArray(tenant?.custom_payment_methods) ? tenant?.custom_payment_methods : []).map(m => ({ v: m.id, l: m.label }))),
          ].map((m) => (
            <button key={m.v} data-testid={`pay-method-${m.v}`} onClick={() => setMethod(m.v)} className={`h-14 rounded-md font-medium btn-tactile ${method === m.v ? "bg-velta-ink text-white" : "bg-white border border-velta-border text-velta-ink hover:border-velta-ink"}`}>
              {m.l}
            </button>
          ))}
        </div>
        {method === "cash" && (
          <div className="mb-4">
            <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">Contante ricevuto</label>
            <input data-testid="pay-cash-input" autoFocus type="number" step="0.01" value={cash} onChange={(e) => setCash(e.target.value)} className="h-14 w-full bg-white border-2 border-velta-border rounded-md px-4 font-mono text-2xl text-right outline-none focus:border-[#1E2D5B]" placeholder={String(total)} />
            <div className="mt-2 flex justify-between items-baseline">
              <span className="text-xs font-mono uppercase tracking-widest text-velta-mute">Resto</span>
              <span data-testid="pay-change" className="font-mono font-bold text-2xl">{formatEUR(change)}</span>
            </div>
          </div>
        )}
        {method === "mixed" && (
          <div className="space-y-2 mb-4">
            {["cash", "card", "ticket"].map((k) => (
              <div key={k} className="flex items-center gap-3">
                <span className="w-20 text-xs font-mono uppercase tracking-widest text-velta-mute">{k}</span>
                <input type="number" step="0.01" value={mixed[k]} onChange={(e) => setMixed({ ...mixed, [k]: parseFloat(e.target.value) || 0 })} className="h-11 flex-1 bg-white border border-velta-border rounded-md px-3 font-mono outline-none focus:border-[#1E2D5B]" />
              </div>
            ))}
            <div className="text-right text-xs font-mono text-velta-mute">Somma: {formatEUR(mixedSum)} / {formatEUR(total)}</div>
          </div>
        )}
        <button data-testid="pay-confirm-btn" onClick={submit} className="w-full h-14 bg-[#1E2D5B] hover:bg-[#162244] text-white rounded-md font-medium text-lg btn-tactile">
          Emetti scontrino
        </button>
      </div>
    </div>
  );
}

function CartFullModal({ order, activeTable, takeaway, hasPreconto, gestionaleEnabled, onClose, onIncLine, onDelLine, onNoteUpdate, onSendComande, onPreconto, onPay, onCancel }) {
  const [noteIdx, setNoteIdx] = React.useState(null);
  if (!order) return null;
  const lines = order.lines || [];
  const totalPieces = lines.reduce((s, l) => s + (l.qty || 0), 0);
  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50" onClick={onClose} data-testid="cart-full-modal">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[92vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-velta-border flex items-center justify-between bg-velta-bg">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-velta-mute mb-1">
              {takeaway ? "Ordine da asporto" : activeTable ? `Tavolo ${activeTable.name}${activeTable.room ? " · " + activeTable.room : ""}` : "Conto attivo"}
            </div>
            <h2 className="font-heading text-2xl font-bold tracking-tight">Dettaglio conto completo</h2>
            <div className="text-xs font-mono text-velta-mute mt-1">
              {lines.length} articol{lines.length === 1 ? "o" : "i"} · {totalPieces} pezz{totalPieces === 1 ? "o" : "i"}
              {order.id && <> · <span className="opacity-60">#{order.id.slice(0, 8)}</span></>}
            </div>
          </div>
          <button data-testid="cart-full-close" onClick={onClose} className="h-10 w-10 rounded-md border border-velta-border hover:bg-white flex items-center justify-center">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-auto p-5">
          {lines.length === 0 ? (
            <div className="text-center text-velta-mute py-16">Il conto è vuoto.</div>
          ) : (
            <div className="space-y-3">
              {groupLinesByCourse(lines).map((g) => (
                <div key={g.key}>
                  {g.label && <div className="text-[10px] font-mono uppercase tracking-widest text-velta-mute mb-1">{g.label}</div>}
                  {(Array.isArray(g.items) ? g.items : []).map(({ line: l, index: i }) => (
                <div key={i} className={`border rounded-md p-4 bg-white ${l.note || noteIdx === i ? "border-velta-ink" : "border-velta-border"}`} data-testid={`cart-full-line-${i}`}>
                  <div className="flex items-center gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="font-heading font-semibold text-lg">{l.name}</div>
                      {l.note && noteIdx !== i && (
                        <div className="text-sm text-velta-ink mt-0.5 italic">↳ {l.note}</div>
                      )}
                      <div className="text-xs font-mono text-velta-mute mt-1">
                        Prezzo unit. {formatEUR(l.price)} · IVA {l.vat}%
                        {l.print_to && <> · comanda → <strong>{l.print_to}</strong></>}
                      </div>
                    </div>
                    <div className="inline-flex items-center border-2 border-velta-border rounded-md bg-velta-bg">
                      <button data-testid={`cart-full-minus-${i}`} onClick={() => onIncLine(i, -1)} className="w-11 h-11 flex items-center justify-center hover:bg-white text-lg font-bold">
                        <Minus className="w-5 h-5" />
                      </button>
                      <span className="w-12 text-center font-mono font-bold text-xl">{l.qty}</span>
                      <button data-testid={`cart-full-plus-${i}`} onClick={() => onIncLine(i, 1)} className="w-11 h-11 flex items-center justify-center hover:bg-white text-lg font-bold">
                        <Plus className="w-5 h-5" />
                      </button>
                    </div>
                    <div className="w-24 text-right">
                      <div className="font-mono font-bold text-lg tabular-nums">{formatEUR(l.price * l.qty)}</div>
                    </div>
                    <button
                      data-testid={`cart-full-note-${i}`}
                      onClick={() => setNoteIdx(noteIdx === i ? null : i)}
                      className={`h-11 w-11 rounded-md border flex items-center justify-center btn-tactile ${l.note ? "bg-velta-ink text-white border-velta-ink" : "bg-white border-velta-border text-velta-mute hover:border-velta-ink hover:text-velta-ink"}`}
                      title="Nota cucina"
                    >
                      <StickyNote className="w-5 h-5" />
                    </button>
                    <button
                      data-testid={`cart-full-del-${i}`}
                      onClick={() => onDelLine(i)}
                      className="h-11 w-11 rounded-md bg-white border border-velta-error text-velta-error hover:bg-velta-error hover:text-white flex items-center justify-center btn-tactile"
                      title="Elimina questo articolo"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                  {noteIdx === i && (
                    <div className="mt-3 pt-3 border-t border-velta-border/50">
                      <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">Nota per la cucina</label>
                      <input
                        autoFocus
                        data-testid={`cart-full-note-input-${i}`}
                        type="text"
                        maxLength={100}
                        defaultValue={l.note || ""}
                        onBlur={(e) => { onNoteUpdate?.(i, e.target.value.trim()); setNoteIdx(null); }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") { onNoteUpdate?.(i, e.currentTarget.value.trim()); setNoteIdx(null); }
                          else if (e.key === "Escape") { setNoteIdx(null); }
                        }}
                        placeholder="Es. ben cotto, senza cipolla, poco piccante, senza glutine..."
                        className="h-11 w-full bg-white border border-velta-border rounded-md px-3 outline-none focus:border-[#1E2D5B]"
                      />
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {["ben cotto", "al sangue", "senza cipolla", "senza glutine", "poco piccante", "senza sale", "senza aglio", "senza lattosio"].map((sug) => (
                          <button
                            key={sug}
                            data-testid={`cart-full-note-sug-${i}-${sug.replace(/ /g, '-')}`}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => { onNoteUpdate?.(i, sug); setNoteIdx(null); }}
                            className="text-xs px-3 h-8 bg-white border border-velta-border rounded-full hover:border-velta-ink hover:bg-velta-ink hover:text-white"
                          >
                            {sug}
                          </button>
                        ))}
                        {l.note && (
                          <button
                            data-testid={`cart-full-note-clear-${i}`}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => { onNoteUpdate?.(i, ""); setNoteIdx(null); }}
                            className="text-xs px-3 h-8 bg-white border border-velta-error text-velta-error rounded-full hover:bg-velta-error hover:text-white ml-auto"
                          >
                            Rimuovi nota
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                    ))}
                  </div>
                ))}
              </div>
            )}
        </div>

        <div className="border-t border-velta-border p-5 bg-velta-bg">
          <div className="flex items-center justify-between mb-4">
            <span className="text-sm font-mono uppercase tracking-widest text-velta-mute">Totale conto</span>
            <span data-testid="cart-full-total" className="font-mono font-bold text-4xl tracking-tighter tabular-nums">{formatEUR(order.total || 0)}</span>
          </div>
          <div className={`grid ${hasPreconto ? "grid-cols-4" : "grid-cols-3"} gap-2`}>
            <button data-testid="cart-full-send" disabled={!lines.length} onClick={onSendComande} className="h-12 bg-velta-ink text-white rounded-md font-medium btn-tactile disabled:opacity-40">Invia Comanda</button>
            {hasPreconto && (
              <button data-testid="cart-full-preconto" disabled={!lines.length} onClick={onPreconto} className="h-12 bg-white border-2 border-velta-ink text-velta-ink hover:bg-velta-ink hover:text-white rounded-md font-medium flex items-center justify-center gap-1 btn-tactile disabled:opacity-40">
                <FileText className="w-4 h-4" /> Preconto
              </button>
            )}
            <button data-testid="cart-full-cancel" disabled={!lines.length} onClick={onCancel} className="h-12 bg-white border border-velta-error text-velta-error hover:bg-velta-error hover:text-white rounded-md font-medium btn-tactile disabled:opacity-40">Annulla ordine</button>
            <button data-testid="cart-full-pay" disabled={!lines.length} onClick={onPay} className="h-12 bg-[#1E2D5B] hover:bg-[#162244] text-white rounded-md font-medium flex items-center justify-center gap-1 btn-tactile disabled:opacity-40">
              <Receipt className="w-4 h-4" /> Scontrino
            </button>
          </div>
          {gestionaleEnabled && (
            <div className="text-[11px] text-velta-mute text-center mt-3 select-none">
              Per <strong>Movimento Gestionale</strong>: chiudi questa vista e usa il gesto nascosto sulla scritta <strong>SCONTRINO</strong> sopra il tastierino (2,5 s su touch, doppio click su PC).
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

